/**
 * Wave RRRR (2026-05-16) · PM notification dropdown panel.
 *
 * Renders inside NotificationsBell when the bell is clicked.
 * Shows the last 20 notifications, supports "Marcar todas como lidas".
 *
 * Wave POS fix (2026-05-17): sanitize notification.title so raw backend
 * kind enums (e.g. `stage.slip.warn`, `supplier.no_response`) never reach
 * the PM. sanitizeNotificationTitle falls back to KIND_NOTIFICATION_LABELS.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { cn } from '@/lib/cn';
import { useT } from '@/hooks/useT';
import { sanitizeImpactReason } from '@/lib/inbox-humanize';
import { parseServerDate } from '@/lib/parse-server-date';
import { Skeleton } from '@/components/ui/Skeleton';

type UserNotification = Awaited<
  ReturnType<typeof api.listUserNotifications>
>['notifications'][number];

const KIND_ICON: Record<string, string> = {
  supplier_no_response: '🛒',
  rdo_missing: '📋',
  doc_expiring: '📄',
  rfi_unresolved: '🔧',
  fvs_overdue: '✅',
  budget_delta_warn: '💰',
};

// Human PT-BR fallback labels for notification kinds
const KIND_NOTIFICATION_LABELS_PT: Record<string, string> = {
  supplier_no_response: 'Fornecedor sem resposta',
  rdo_missing:          'RDO atrasado',
  doc_expiring:         'Documento vencendo',
  rfi_unresolved:       'Pedido de informação aberto',
  fvs_overdue:          'Vistoria atrasada',
  budget_delta_warn:    'Desvio de orçamento',
  stage_slip_warn:      'Etapa atrasando',
  budget_overrun:       'Orçamento estourou',
  cost_variance_high:   'Custo fora do previsto',
  quality_rejection_spike: 'Pico de rejeição na qualidade',
  // Wave 48 · proactive nudge kinds
  proactive_sla_breach:           'SLA excedido',
  proactive_rdo_late:             'RDO atrasado',
  proactive_anomaly_open:         'Anomalia aberta',
  proactive_measurement_overdue:  'Medição atrasada',
  proactive_supplier_pending:     'Fornecedor pendente',
  persona_weekly:                 'Resumo semanal',
};

const KIND_NOTIFICATION_LABELS_EN: Record<string, string> = {
  supplier_no_response: 'Supplier not responding',
  rdo_missing:          'Daily log overdue',
  doc_expiring:         'Document expiring',
  rfi_unresolved:       'Open request for information',
  fvs_overdue:          'Inspection overdue',
  budget_delta_warn:    'Budget variance',
  stage_slip_warn:      'Stage running late',
  budget_overrun:       'Budget overrun',
  cost_variance_high:   'Cost out of plan',
  quality_rejection_spike: 'Quality rejection spike',
  proactive_sla_breach:           'SLA exceeded',
  proactive_rdo_late:             'Daily log late',
  proactive_anomaly_open:         'Open issue',
  proactive_measurement_overdue:  'Measurement overdue',
  proactive_supplier_pending:     'Supplier pending',
  persona_weekly:                 'Weekly summary',
};

function sanitizeNotificationTitle(item: UserNotification, isEn: boolean): string {
  const clean = sanitizeImpactReason(item.title);
  if (clean) return clean;
  const labels = isEn ? KIND_NOTIFICATION_LABELS_EN : KIND_NOTIFICATION_LABELS_PT;
  return labels[item.kind] ?? (isEn ? 'Notification' : 'Notificação');
}

function timeAgo(isoDate: string, isEn: boolean): string {
  // parseServerDate trata timestamps SQLite sem timezone como UTC — sem
  // ele, `new Date('2026-04-28 02:00:00')` parseia como local e o "agora/m/h"
  // fica deslocado pelo offset do browser (mesmo bug que Thread.tsx resolveu).
  const parsed = parseServerDate(isoDate);
  if (!parsed) return '';
  const diff = Date.now() - parsed.getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return isEn ? 'now' : 'agora';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

interface Props {
  onClose: () => void;
  onCountChange?: (count: number) => void;
}

export function NotificationDropdown({ onClose, onCountChange }: Props) {
  const { t, locale } = useT();
  const isEn = locale === 'en';
  const navigate = useNavigate();
  const [items, setItems] = useState<UserNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.listUserNotifications({ limit: 20 });
      setItems(result.notifications);
      const unread = result.notifications.filter((n) => !n.readAt).length;
      onCountChange?.(unread);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleClick = async (item: UserNotification) => {
    if (!item.readAt) {
      try {
        await api.markUserNotificationRead(item.id);
        setItems((prev) =>
          prev.map((n) =>
            n.id === item.id ? { ...n, readAt: new Date().toISOString() } : n,
          ),
        );
        const remaining = items.filter((n) => n.id !== item.id && !n.readAt).length;
        onCountChange?.(remaining);
      } catch {
        // Best-effort; navigate anyway.
      }
    }
    // Sempre dá um destino: se a notificação tem href próprio, vai pra lá;
    // senão cai na central de avisos (em vez de só fechar sem feedback).
    navigate(item.href ?? '/inbox?tab=avisos');
    onClose();
  };

  const handleMarkAll = async () => {
    setMarkingAll(true);
    try {
      await api.markAllUserNotificationsRead();
      setItems((prev) => prev.map((n) => ({ ...n, readAt: n.readAt ?? new Date().toISOString() })));
      onCountChange?.(0);
    } catch {
      // Best-effort.
    } finally {
      setMarkingAll(false);
    }
  };

  const unreadCount = items.filter((n) => !n.readAt).length;

  return (
    // listen-to-JP-fix · 2026-05-20 — z-50 unifies this panel with the
    // rest of the TopBar dropdowns (ProjectSwitcher, InboxBell, Avatar).
    // Without it, the bell pinned to the far right was visually correct
    // but the same bug applied symmetrically to ProjectSwitcher on the
    // left; the audit fixes them as a set.
    <div
      role="menu"
      data-testid="notification-dropdown-panel"
      style={{ transformOrigin: 'top right' }}
      className={cn(
        'absolute right-0 mt-2 w-80 overflow-hidden rounded-xl border border-line z-50',
        'bg-surface-2 shadow-pop dark:shadow-pop-dark animate-scale-in',
        'flex flex-col max-h-[480px]',
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-line px-3 py-2.5">
        <span className="text-sm font-semibold text-ink">
          {t.notificationsBell.title}
        </span>
        {unreadCount > 0 && (
          <button
            type="button"
            disabled={markingAll}
            onClick={handleMarkAll}
            className="text-xs text-brand hover:underline disabled:opacity-50"
          >
            {t.notificationsBell.markAllRead}
          </button>
        )}
      </div>

      {/* Body */}
      <div className="overflow-y-auto flex-1">
        {loading && (
          <div className="flex flex-col gap-0 divide-y divide-line">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-start gap-2 px-3 py-2.5">
                <Skeleton className="h-5 w-5 shrink-0 rounded-full mt-0.5" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-3/4 rounded" />
                  <Skeleton className="h-3 w-1/2 rounded" />
                </div>
              </div>
            ))}
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 gap-2">
            <span className="text-2xl" aria-hidden="true">🔔</span>
            <span className="text-xs text-ink-muted">{t.notificationsBell.empty}</span>
          </div>
        )}
        {!loading &&
          items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              onClick={() => void handleClick(item)}
              className={cn(
                'w-full text-left px-3 py-2.5 border-b border-line last:border-0',
                'hover:bg-surface-3 transition-colors duration-100',
                !item.readAt && 'bg-brand/5',
              )}
            >
              <div className="flex items-start gap-2">
                <span className="text-base leading-none mt-0.5" aria-hidden="true">
                  {KIND_ICON[item.kind] ?? '🔔'}
                </span>
                <div className="flex-1 min-w-0">
                  <p
                    className={cn(
                      'text-sm truncate',
                      item.readAt ? 'text-ink-muted font-normal' : 'text-ink font-medium',
                    )}
                  >
                    {sanitizeNotificationTitle(item, isEn)}
                  </p>
                  {item.body && (
                    <p className="text-xs text-ink-muted line-clamp-2 mt-0.5">{sanitizeImpactReason(item.body) ?? item.body}</p>
                  )}
                </div>
                <span className="text-[10px] text-ink-subtle shrink-0 mt-0.5">
                  {timeAgo(item.createdAt, isEn)}
                </span>
              </div>
              {!item.readAt && (
                <span className="sr-only">{t.notificationsBell.unread}</span>
              )}
            </button>
          ))}
      </div>

      {/* Footer */}
      <div className="border-t border-line px-4 py-2 text-center">
        <Link to="/inbox?tab=avisos" onClick={onClose} className="text-sm text-brand hover:underline">
          {isEn ? 'View all notices →' : 'Ver todos os avisos →'}
        </Link>
      </div>
    </div>
  );
}
